import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  getFacilityMap,
  getMapAnalytics,
  upsertFacilityMap,
  upsertZone,
  deleteZone,
} from "@/lib/warehouse-map.functions";
import { useScope } from "@/hooks/use-scope";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { WarehouseCanvas } from "@/components/warehouse-map/canvas";

export const Route = createFileRoute("/_authenticated/app/warehouse-map/")({ component: Builder });

const ZONE_KINDS = ["receiving","storage","pick_face","packing","returns","kitting","staging","shipping"] as const;
type ZoneKind = typeof ZONE_KINDS[number];

function Builder() {
  const { tenantId, facilityId } = useScope();
  const qc = useQueryClient();
  const getMap = useServerFn(getFacilityMap);
  const getAnalytics = useServerFn(getMapAnalytics);
  const saveMap = useServerFn(upsertFacilityMap);
  const saveZone = useServerFn(upsertZone);
  const removeZone = useServerFn(deleteZone);
  const [selected, setSelected] = useState<{ kind: "zone" | "location"; id: string } | null>(null);
  const [drawKind, setDrawKind] = useState<ZoneKind | null>(null);
  const [editingMap, setEditingMap] = useState(false);
  // 1 cell = 1 foot. Stored as 30cm (closest int ≥ schema min of 10cm) for legacy compat.
  const FOOT_CM = 30;
  const [mapForm, setMapForm] = useState({ cols: 60, rows: 40 });

  const { data, isLoading } = useQuery({
    queryKey: ["wm-map", tenantId, facilityId],
    enabled: !!tenantId && !!facilityId,
    queryFn: () => getMap({ data: { tenantId: tenantId!, facilityId: facilityId! } }),
  });
  const { data: analytics } = useQuery({
    queryKey: ["wm-analytics", tenantId, facilityId],
    enabled: !!tenantId && !!facilityId,
    queryFn: () => getAnalytics({ data: { tenantId: tenantId!, facilityId: facilityId! } }),
  });

  useEffect(() => {
    if (data?.map) setMapForm({ cols: data.map.grid_cols, rows: data.map.grid_rows });
  }, [data?.map]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setDrawKind(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const invalidate = () => qc.invalidateQueries({ queryKey: ["wm-map", tenantId, facilityId] });

  const mapMutation = useMutation({
    mutationFn: () => saveMap({ data: {
      tenantId: tenantId!, facilityId: facilityId!,
      grid_cols: mapForm.cols, grid_rows: mapForm.rows, cell_size_cm: FOOT_CM,
    }}),
    onSuccess: () => { toast.success("Map size saved"); setEditingMap(false); invalidate(); },
    onError: (e: Error) => toast.error(e.message),
  });

  const zoneCreateMutation = useMutation({
    mutationFn: (input: { kind: ZoneKind; rect: { x: number; y: number; w: number; h: number } }) => {
      const existing = (data?.zones ?? []).filter((z) => z.kind === input.kind).length + 1;
      const code = `${input.kind.slice(0,3).toUpperCase()}-${String(existing).padStart(2,"0")}`;
      return saveZone({ data: {
        tenantId: tenantId!, facilityId: facilityId!,
        code, name: code, kind: input.kind, pick_sequence: 100,
        grid_x: input.rect.x, grid_y: input.rect.y,
        grid_w: input.rect.w, grid_h: input.rect.h, active: true,
      }});
    },
    onSuccess: () => { toast.success("Zone added"); setDrawKind(null); invalidate(); },
    onError: (e: Error) => toast.error(e.message),
  });

  const sel = selected && data
    ? selected.kind === "zone"
      ? data.zones.find((z) => z.id === selected.id)
      : data.locations.find((l) => l.id === selected.id)
    : null;

  type ZoneRow = NonNullable<typeof data>["zones"][number];
  const selZone = selected?.kind === "zone" && sel ? (sel as ZoneRow) : null;

  return (
    <div className="flex flex-col h-[calc(100vh-3.5rem)] gap-2 p-2">
      {/* Top compact analytics strip */}
      <Card className="shrink-0">
        <CardContent className="p-2 grid grid-cols-2 sm:grid-cols-5 gap-4 text-sm">
          <Stat label="Total locations" value={analytics?.totalLocations ?? "—"} />
          <Stat label="Pickable" value={analytics?.pickableLocations ?? "—"} />
          <Stat label="Inactive" value={analytics?.inactiveLocations ?? "—"} />
          <Stat label="Active zones" value={analytics?.activeZones ?? "—"} />
          <Stat label="Hot · 30d" value={analytics?.hotLocations30d ?? "—"} />
        </CardContent>
      </Card>

      <div className="grid grid-cols-12 gap-2 flex-1 min-h-0">
      {/* Left palette */}
      <Card className="col-span-2 overflow-auto">
        <CardContent className="p-3 space-y-3 text-sm">
          <div className="flex items-center justify-between">
            <div className="font-semibold">Map</div>
            <Button size="sm" variant="ghost" className="h-6 px-2 text-xs"
              onClick={() => setEditingMap((v) => !v)}>
              {editingMap ? "Close" : "Resize"}
            </Button>
          </div>
          {editingMap ? (
            <div className="space-y-2">
              <div>
                <Label className="text-xs">Width (ft)</Label>
                <Input type="number" min={10} max={500} value={mapForm.cols}
                  onChange={(e) => setMapForm({ ...mapForm, cols: Number(e.target.value) || 0 })} />
              </div>
              <div>
                <Label className="text-xs">Length (ft)</Label>
                <Input type="number" min={10} max={500} value={mapForm.rows}
                  onChange={(e) => setMapForm({ ...mapForm, rows: Number(e.target.value) || 0 })} />
              </div>
              <div className="text-xs text-muted-foreground">
                {mapForm.cols} ft × {mapForm.rows} ft · {(mapForm.cols * mapForm.rows).toLocaleString()} sq ft
              </div>
              <Button size="sm" className="w-full" disabled={mapMutation.isPending}
                onClick={() => mapMutation.mutate()}>
                {mapMutation.isPending ? "Saving…" : "Save size"}
              </Button>
            </div>
          ) : (
            <div className="text-xs text-muted-foreground">
              {data?.map
                ? `${data.map.grid_cols} × ${data.map.grid_rows} ft · ${(data.map.grid_cols * data.map.grid_rows).toLocaleString()} sq ft`
                : "—"}
            </div>
          )}

          <div className="pt-2 border-t border-border font-semibold">Draw zone</div>
          <div className="space-y-1">
            {ZONE_KINDS.map((k) => (
              <button key={k}
                onClick={() => setDrawKind(drawKind === k ? null : k)}
                className={`w-full text-left flex items-center gap-2 px-2 py-1 rounded border capitalize text-xs transition ${
                  drawKind === k
                    ? "border-primary bg-primary/10 text-foreground"
                    : "border-border bg-card hover:bg-accent text-muted-foreground"
                }`}>
                {k.replace("_"," ")}
              </button>
            ))}
          </div>

          <div className="pt-2 border-t border-border space-y-1">
            <Link to="/app/warehouse-map/locations" className="block text-xs text-primary hover:underline">
              Bulk create locations →
            </Link>
          </div>
        </CardContent>
      </Card>

      {/* Canvas */}
      <div className="col-span-8 min-h-0 h-full flex flex-col">
        {isLoading || !data ? (
          <div className="flex-1 grid place-items-center text-sm text-muted-foreground border border-dashed border-border rounded-md">
            Loading map…
          </div>
        ) : (
          <WarehouseCanvas
            cols={data.map.grid_cols}
            rows={data.map.grid_rows}
            zones={data.zones}
            locations={data.locations}
            selectedId={selected?.id ?? null}
            onSelect={(kind, id) => setSelected({ kind, id })}
            drawKind={drawKind}
            onDrawComplete={(rect) => {
              if (!drawKind) return;
              if (rect.w < 1 || rect.h < 1) return;
              zoneCreateMutation.mutate({ kind: drawKind, rect });
            }}
          />
        )}
      </div>

      {/* Right details */}
      <Card className="col-span-2 overflow-auto">
        <CardContent className="p-3 text-sm space-y-2">
          <div className="font-semibold">Details</div>
          {!sel && <p className="text-muted-foreground text-xs">Select a zone or location.</p>}
          {selZone ? (
            <ZoneEditor
              key={selZone.id}
              zone={selZone}
              onSave={async (patch) => {
                await saveZone({ data: {
                  id: selZone.id,
                  tenantId: tenantId!, facilityId: facilityId!,
                  code: patch.code, name: patch.name, kind: selZone.kind as ZoneKind,
                  pick_sequence: selZone.pick_sequence ?? 100,
                  grid_x: patch.x, grid_y: patch.y, grid_w: patch.w, grid_h: patch.h,
                  active: selZone.active,
                }});
                toast.success("Zone updated");
                invalidate();
              }}
              onDelete={async () => {
                await removeZone({ data: { id: selZone.id, tenantId: tenantId! } });
                toast.success("Zone deleted");
                setSelected(null);
                invalidate();
              }}
            />
          ) : sel ? (
            <div className="space-y-1">
              <div className="font-mono text-xs">{(sel as { code: string }).code}</div>
              <div className="text-xs text-muted-foreground">
                {(sel as { pickable: boolean }).pickable ? "Pickable" : "Non-pickable"}
                {" · "}
                {(sel as { active: boolean }).active ? "Active" : "Inactive"}
              </div>
              <Link
                to="/app/warehouse-map/locations"
                className="text-xs text-primary hover:underline"
              >Edit in locations table →</Link>
            </div>
          ) : null}
        </CardContent>
      </Card>
      </div>
    </div>
  );
}

function ZoneEditor({
  zone, onSave, onDelete,
}: {
  zone: { id: string; code: string; name: string; kind: string | null; grid_x: number | null; grid_y: number | null; grid_w: number | null; grid_h: number | null };
  onSave: (p: { code: string; name: string; x: number; y: number; w: number; h: number }) => Promise<void>;
  onDelete: () => Promise<void>;
}) {
  const [form, setForm] = useState({
    code: zone.code, name: zone.name,
    x: zone.grid_x ?? 0, y: zone.grid_y ?? 0,
    w: zone.grid_w ?? 1, h: zone.grid_h ?? 1,
  });
  const [busy, setBusy] = useState(false);
  return (
    <div className="space-y-2">
      <div className="text-xs text-muted-foreground capitalize">Zone · {zone.kind ?? "—"}</div>
      <div>
        <Label className="text-xs">Code</Label>
        <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} />
      </div>
      <div>
        <Label className="text-xs">Name</Label>
        <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-xs">X</Label>
          <Input type="number" value={form.x} onChange={(e) => setForm({ ...form, x: Number(e.target.value) || 0 })} />
        </div>
        <div>
          <Label className="text-xs">Y</Label>
          <Input type="number" value={form.y} onChange={(e) => setForm({ ...form, y: Number(e.target.value) || 0 })} />
        </div>
        <div>
          <Label className="text-xs">Width</Label>
          <Input type="number" value={form.w} onChange={(e) => setForm({ ...form, w: Number(e.target.value) || 1 })} />
        </div>
        <div>
          <Label className="text-xs">Height</Label>
          <Input type="number" value={form.h} onChange={(e) => setForm({ ...form, h: Number(e.target.value) || 1 })} />
        </div>
      </div>
      <div className="flex gap-2 pt-1">
        <Button size="sm" className="flex-1" disabled={busy}
          onClick={async () => { setBusy(true); try { await onSave(form); } finally { setBusy(false); } }}>
          Save
        </Button>
        <Button size="sm" variant="destructive" disabled={busy}
          onClick={async () => { setBusy(true); try { await onDelete(); } finally { setBusy(false); } }}>
          Delete
        </Button>
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number | string }) {
  return (
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-xl font-semibold tabular-nums">{value}</div>
    </div>
  );
}